import * as Yup from "yup";
import { FIELD_LIMITS, nameField } from "./yupCommonFields";

const DETECT_OPS = new Set(["detectSQLi", "detectXSS"]);

function operatorNeedsValue(operator) {
  const op = String(operator ?? "");
  if (!op || op === "none") return false;
  if (DETECT_OPS.has(op)) return false;
  if (op.startsWith("!") && DETECT_OPS.has(op.slice(1))) return false;
  return true;
}

const customRuleRowSchema = Yup.object({
  key: Yup.array().default([]),
  operator: Yup.string().default("none"),
  value: Yup.string().default(""),
})
  .test("row-key", function (row) {
    const keyEmpty = !row?.key?.length || row.key[0] === "none";
    if (keyEmpty) {
      return this.createError({ path: `${this.path}.key`, message: "Select a field" });
    }
    return true;
  })
  .test("row-operator", function (row) {
    const keyEmpty = !row?.key?.length || row.key[0] === "none";
    if (keyEmpty) return true;
    const opEmpty = !row.operator || row.operator === "none";
    if (opEmpty) {
      return this.createError({ path: `${this.path}.operator`, message: "Select an operator" });
    }
    return true;
  })
  .test("row-value", function (row) {
    const keyEmpty = !row?.key?.length || row.key[0] === "none";
    const opEmpty = !row.operator || row.operator === "none";
    if (keyEmpty || opEmpty) return true;

    if (operatorNeedsValue(row.operator) && !String(row.value ?? "").trim()) {
      return this.createError({ path: `${this.path}.value`, message: "Value is required" });
    }
    if (operatorNeedsValue(row.operator) && String(row.value ?? "").length > FIELD_LIMITS.customRuleValue) {
      return this.createError({
        path: `${this.path}.value`,
        message: `Value must be ${FIELD_LIMITS.customRuleValue} characters or fewer`,
      });
    }
    return true;
  });

export const customRuleFormSchema = Yup.object({
  name: nameField,
  conditions: Yup.array().of(customRuleRowSchema),
});

function mapCustomRuleYupError(err, rowCount) {
  const formErrors = {};
  const rowErrors = Array.from({ length: rowCount }, () => ({}));
  const errors = err.inner?.length ? err.inner : [err];

  for (const e of errors) {
    const path = e.path || "";
    const match = path.match(/^conditions\[(\d+)\]\.(\w+)$/);
    if (match) {
      const idx = Number(match[1]);
      const key = match[2];
      if (idx < rowErrors.length) rowErrors[idx][key] = e.message;
      continue;
    }
    if (path === "name") formErrors.name = e.message;
  }

  return { valid: false, formErrors, rowErrors };
}

export function validateCustomRuleForm({ name, conditions }) {
  const values = { name, conditions: conditions || [] };
  const rowCount = values.conditions.length;

  try {
    customRuleFormSchema.validateSync(values, { abortEarly: false });
    return {
      valid: true,
      formErrors: {},
      rowErrors: values.conditions.map(() => ({})),
    };
  } catch (err) {
    if (err.name !== "ValidationError") throw err;
    return mapCustomRuleYupError(err, rowCount);
  }
}
